/**
 * ApproxStartHeuristicsAdminPage · iter303ak-fix23f
 * ─────────────────────────────────────────────────
 * Simple admin editor for the approximate-start-time heuristic rules
 * used by Accepted T10 A15 (ApproxStartEstimator). Two editable tables:
 *   • Presets — the bucket dropdown options (label + color)
 *   • Rules   — top-down evaluation list (match_quote_types + season →
 *               preset_key)
 *
 * On Save PUTs the whole config to /app-settings/approx-start-heuristics.
 * Backend validates each rule references a known preset_key.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw, AlertCircle, CheckCircle2 } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const COLOR_OPTIONS = ['rose', 'amber', 'orange', 'purple', 'sky', 'emerald', 'slate', 'indigo'];
const SEASON_OPTIONS = [
  { value: 'any',  label: 'Any' },
  { value: 'warm', label: 'Warm (Nov-Mar)' },
  { value: 'cool', label: 'Cool (Apr-Oct)' },
];
const QUOTE_TYPES = ['Re-Roof', 'Restoration', 'Guttering', 'Maintenance', 'Concrete', 'Iron', 'Painter', 'Other'];

export default function ApproxStartHeuristicsAdminPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await api.get('/app-settings/approx-start-heuristics');
      setCfg(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true); setErr(''); setOk('');
    try {
      await api.put('/app-settings/approx-start-heuristics', cfg);
      setOk('Saved.');
      setTimeout(() => setOk(''), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const updatePreset = (i, patch) => setCfg(c => ({
    ...c,
    presets: c.presets.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
  }));
  const removePreset = (i) => setCfg(c => ({
    ...c,
    presets: c.presets.filter((_, idx) => idx !== i),
  }));
  const addPreset = () => setCfg(c => ({
    ...c,
    presets: [...c.presets, { key: `new_${Date.now()}`, label: 'New bucket', color: 'slate' }],
  }));

  const updateRule = (i, patch) => setCfg(c => ({
    ...c,
    rules: c.rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
  }));
  const removeRule = (i) => setCfg(c => ({
    ...c,
    rules: c.rules.filter((_, idx) => idx !== i),
  }));
  const moveRule = (i, dir) => setCfg(c => {
    const j = i + dir;
    if (j < 0 || j >= c.rules.length) return c;
    const next = [...c.rules];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...c, rules: next };
  });
  const addRule = () => setCfg(c => ({
    ...c,
    rules: [...c.rules, { name: 'New rule', match_quote_types: [], season: 'any', preset_key: c.presets[0]?.key || '' }],
  }));

  const toggleRuleType = (i, qt) => {
    const cur = new Set(cfg.rules[i].match_quote_types || []);
    if (cur.has(qt)) cur.delete(qt);
    else cur.add(qt);
    updateRule(i, { match_quote_types: Array.from(cur) });
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="approx-start-admin-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Approximate Start-Time Heuristics</h1>
        <p className="text-sm text-slate-600 mt-1">
          Rules used by the Accepted T10 · Approx Start-Time Estimator to auto-suggest a start-time bucket for the client-facing Acceptance Summary letter. Rules are evaluated top-to-bottom; first match wins. Season derives from the current month (Nov-Mar = warm).
        </p>
      </div>

      {err && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="approx-start-admin-err">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}
      {ok && (
        <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="approx-start-admin-ok">
          <CheckCircle2 className="w-4 h-4 mt-0.5" /> <span>{ok}</span>
        </div>
      )}

      {loading && <div className="text-sm text-slate-400 italic">Loading…</div>}

      {cfg && (
        <>
          <section className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-slate-800">Presets (bucket options)</h2>
              <button onClick={addPreset} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-100 text-slate-700 border border-slate-300 rounded hover:bg-slate-200" data-testid="approx-start-admin-add-preset">
                <Plus className="w-3.5 h-3.5" /> Add preset
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-2 py-1 w-40">Key</th>
                    <th className="text-left px-2 py-1">Label</th>
                    <th className="text-left px-2 py-1 w-32">Color</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {cfg.presets.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`approx-start-admin-preset-row-${i}`}>
                      <td className="px-2 py-1"><input value={p.key} onChange={(e) => updatePreset(i, { key: e.target.value })} className="w-full px-1.5 py-0.5 border border-slate-300 rounded text-xs" /></td>
                      <td className="px-2 py-1"><input value={p.label} onChange={(e) => updatePreset(i, { label: e.target.value })} className="w-full px-1.5 py-0.5 border border-slate-300 rounded text-xs" /></td>
                      <td className="px-2 py-1">
                        <select value={p.color} onChange={(e) => updatePreset(i, { color: e.target.value })} className="w-full px-1.5 py-0.5 border border-slate-300 rounded text-xs bg-white">
                          {COLOR_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <button onClick={() => removePreset(i)} className="p-1 text-rose-500 hover:text-rose-700" title="Delete" data-testid={`approx-start-admin-preset-delete-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-slate-800">Rules (top-down evaluation)</h2>
              <button onClick={addRule} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-100 text-slate-700 border border-slate-300 rounded hover:bg-slate-200" data-testid="approx-start-admin-add-rule">
                <Plus className="w-3.5 h-3.5" /> Add rule
              </button>
            </div>
            <div className="space-y-3">
              {cfg.rules.map((r, i) => (
                <div key={i} className="p-3 border border-slate-200 rounded-md bg-slate-50/50 space-y-2" data-testid={`approx-start-admin-rule-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-6 text-[10px] font-bold text-slate-400 text-right">{i + 1}</span>
                    <input
                      value={r.name || ''}
                      onChange={(e) => updateRule(i, { name: e.target.value })}
                      placeholder="Rule name (for logging)"
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm bg-white"
                    />
                    <select value={r.season || 'any'} onChange={(e) => updateRule(i, { season: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white">
                      {SEASON_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <select value={r.preset_key || ''} onChange={(e) => updateRule(i, { preset_key: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white">
                      <option value="">(pick preset)</option>
                      {cfg.presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                    <button onClick={() => moveRule(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveRule(i, 1)} disabled={i === cfg.rules.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => removeRule(i)} className="p-1 text-rose-500 hover:text-rose-700" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="pl-8 flex flex-wrap gap-1.5">
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest mr-1 self-center">Match quote types (any of):</span>
                    {QUOTE_TYPES.map((qt) => {
                      const active = (r.match_quote_types || []).includes(qt);
                      return (
                        <button
                          key={qt}
                          onClick={() => toggleRuleType(i, qt)}
                          className={`px-2 py-0.5 text-[11px] rounded border ${active ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}`}
                          data-testid={`approx-start-admin-rule-${i}-type-${qt}`}
                        >
                          {qt}
                        </button>
                      );
                    })}
                    {(r.match_quote_types || []).length === 0 && (
                      <span className="text-[10px] italic text-amber-700 self-center">(none picked = default fallback)</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="flex items-center gap-3 pt-2">
            <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 border border-slate-300 rounded hover:bg-slate-200" data-testid="approx-start-admin-reload">
              <RotateCcw className="w-4 h-4" /> Reload
            </button>
            <span className="flex-1" />
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40" data-testid="approx-start-admin-save">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
